import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import RotatingBadge from "@/components/RotatingBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function Spotlight() {
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const { add, setOpen } = useCart();

  const product = products[0];
  if (!product) return null;

  const handleAdd = () => {
    add(product);
    toast(`${product.name} added to your bag`, {
      action: { label: "View Bag", onClick: () => setOpen(true) },
    });
  };

  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  return (
    <section data-testid="spotlight-section" className="bg-ink text-bone">
      <div className="mx-auto grid max-w-[1600px] lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="relative overflow-hidden"
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="aspect-[4/3] h-full max-h-[60vh] w-full object-cover transition-transform duration-700 ease-out hover:scale-105 lg:aspect-auto lg:max-h-none"
          />
          <span className="absolute left-6 top-6 bg-ember px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-ink">
            Object of the week
          </span>
          <RotatingBadge className="absolute bottom-4 right-4 h-16 w-16 sm:h-20 sm:w-20 md:bottom-6 md:right-6 md:h-24 md:w-24" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, delay: isTouch ? 0 : 0.15, ease: EASE }}
          className="relative flex flex-col justify-center overflow-hidden px-5 py-12 md:px-16 lg:py-32"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 top-10 h-96 w-96 rounded-full bg-ember/15 blur-3xl"
          />
          <p className="relative text-xs uppercase tracking-[0.3em] text-ember">
            {product.tag}
          </p>
          <h2
            data-testid="spotlight-name"
            className="mt-3 font-serif text-3xl font-medium tracking-tight sm:text-4xl md:mt-4 md:text-6xl"
          >
            {product.name}
          </h2>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-bone/50">
            {product.notes}
          </p>
          <p className="mt-8 max-w-md text-base leading-relaxed text-bone/70">
            {(() => {
              const text = product.description || "";
              if (!text)
                return "Our current favourite — a clever everyday thing that earns its place in the bag, the pocket and the drawer.";
              const words = text.split(/\s+/).slice(0, 30).join(" ");
              return words.length < text.length ? `${words}…` : text;
            })()}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <span
              data-testid="spotlight-price"
              className="font-serif text-4xl font-medium text-ember"
            >
              {fmt(product.price)}
            </span>
            <button
              data-testid="spotlight-add-to-bag"
              onClick={handleAdd}
              className="group inline-flex items-center gap-3 bg-ember px-8 py-4 text-xs uppercase tracking-[0.25em] text-ink transition-colors duration-500 hover:bg-bone"
            >
              Add to Bag
              <ArrowRight
                className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1"
                strokeWidth={1.5}
              />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
